import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import ContentCard from "../../components/ContentCard";
import { getCompanyId } from "../../utils/api";
import { apiRequest } from "../../utils/apiRequest";
import {
  buildPurchaseOrderRequestBody,
  buildSubcontractRequestBody,
} from "../../utils/requestBodies.js";
import { showToast } from "../../utils/toastOptions";
import { AttachFilesSection } from "./AttachFilesSection";
import PurchaseOrderLineItemsTable from "./PurchaseOrderLineItemsTable";
import { ScheduleOfValuesSection } from "./ScheduleOfValuesSection";
import SearchableSelect from "./SearchableSelect";
import { safeDisplay } from "./sharedDropdownOptions";
import SubcontractLineItemsTable from "./SubContractLineItemsTable";

const selectStyle = {
  width: 390,
  height: 36,
  "& .MuiSelect-select": {
    padding: "6px 12px",
  },
};

const inputStyle = {
  width: 390,
  "& .MuiInputBase-root": {
    height: 36,
  },
  "& .MuiInputBase-input": {
    padding: "0 12px",
  },
};

const dateStyle = {
  width: 200,
  "& .MuiInputBase-root": {
    height: 36,
  },
  "& .MuiInputBase-input": {
    padding: "0 12px",
  },
};

const RequiredLabel = ({ label }) => (
  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
    {label} <span style={{ color: "red" }}>*</span>
  </Typography>
);

RequiredLabel.propTypes = {
  label: PropTypes.string.isRequired,
};

const CreateCommitmentsForm = forwardRef(
  (
    {
      formData,
      setFormData,
      commitmentType,
      setCommitmentType,
      purchaseOrderDropdownOptions,
      subcontractDropdownOptions,
      selectedProject,
      setRefreshCommitments,
    },
    ref
  ) => {
    const [localCommitments, setLocalCommitments] = useState(formData.commitments || {});

    useEffect(() => {
      setLocalCommitments(formData.commitments || {});
    }, [formData.commitments]);

    function useDebounce(value, delay = 200) {
      const [debouncedValue, setDebouncedValue] = useState(value);
      useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
      }, [value, delay]);
      return debouncedValue;
    }

    const debouncedCommitments = useDebounce(localCommitments, 200);

    useEffect(() => {
      setFormData((prev) => ({ ...prev, commitments: debouncedCommitments }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedCommitments]);

    const handleChange = (event) => {
      const { name, value, type, checked } = event.target;
      const newValue = ["purchaseContractCompany", "purchaseAssignedTo"].includes(name)
        ? Number(value)
        : value;

      setLocalCommitments((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : newValue,
      }));
    };

    const [scheduleRows, setScheduleRows] = useState([
      { changeEvent: "", budgetCodeId: "", description: "", amount: "", billed: "", remaining: "" },
    ]);

    const handleSelectType = (type) => {
      setCommitmentType(type);
      setLocalCommitments({});
      setFormData((prev) => ({ ...prev, commitments: {} }));
    };

    const handleSubmit = useCallback(async () => {
      if (!selectedProject?.id) {
        showToast("error", "Please select a project before submitting.");
        return;
      }

      if (!commitmentType) {
        showToast("error", "Please select a commitment type (Subcontract or Purchase Order).");
        return;
      }

      try {
        if (commitmentType === "subcontract") {
          await handleSubcontractSubmit();
        } else if (commitmentType === "purchase_order") {
          await handlePurchaseOrderSubmit();
        }
      } catch (error) {
        showToast("error", error.message || "Failed to create commitment");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject, commitmentType, formData]);

    const handleSubcontractSubmit = async () => {
      const requestBody = buildSubcontractRequestBody(selectedProject.id, formData);

      const companyId = getCompanyId(selectedProject);
      if (!companyId) {
        showToast("error", "Project missing Company ID");
        return;
      }
      await apiRequest(
        `${process.env.REACT_APP_API_BASE_URL}/commitments/companies/${companyId}/projects/${selectedProject.id}/subcontracts/`,
        { method: "POST", body: requestBody }
      );

      showToast("success", "Subcontract created successfully!");
      setRefreshCommitments?.((prev) => !prev);
      setFormData((prev) => ({ ...prev, commitments: {} }));
    };

    const handlePurchaseOrderSubmit = async () => {
      const requestBody = buildPurchaseOrderRequestBody(selectedProject.id, formData);

      const companyId = getCompanyId(selectedProject);
      if (!companyId) {
        showToast("error", "Project missing Company ID");
        return;
      }
      await apiRequest(
        `${process.env.REACT_APP_API_BASE_URL}/commitments/companies/${companyId}/projects/${selectedProject.id}/purchase_orders/`,
        { method: "POST", body: requestBody }
      );

      showToast("success", "Purchase Order created successfully!");
      setRefreshCommitments?.((prev) => !prev);
      setFormData((prev) => ({ ...prev, commitments: {} }));
    };

    useImperativeHandle(
      ref,
      () => ({
        handleSubmit,
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [selectedProject, commitmentType]
    );

    const getDisplayText = (value, options, fieldName) => {
      if (value == null || value === "") return "";
      const option = options?.find((opt) => {
        const optId = opt.id;
        const val = value;
        if (!Number.isNaN(optId) && !Number.isNaN(val)) {
          return Number(optId) === Number(val);
        }
        return String(optId) === String(val) || opt.value === val;
      });
      if (!option) return value;
      switch (fieldName) {
        case "budgetCode":
          return safeDisplay(
            option.code ? `${option.code} — ${option.name || ""}` : option.name || value
          );
        case "unitOfMeasure":
          return safeDisplay(option.name || value);
        case "changeEvent":
          return safeDisplay(
            option.number ? `${option.number} — ${option.title || ""}` : option.title || value
          );
        default:
          return safeDisplay(option.name || value);
      }
    };

    const renderForm = () => {
      return (
        <Box>
          <ContentCard elevation={1} p={2} sx={{ mb: 3, bgcolor: "#f5f5f5" }}>
            <Typography variant="h6" gutterBottom>
              Select Commitment Type
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant={commitmentType === "subcontract" ? "contained" : "outlined"}
                onClick={() => handleSelectType("subcontract")}
                sx={{ minWidth: 200 }}
              >
                Create Subcontract
              </Button>
              <Button
                variant={commitmentType === "purchase_order" ? "contained" : "outlined"}
                onClick={() => handleSelectType("purchase_order")}
                sx={{ minWidth: 200 }}
              >
                Create Purchase Order
              </Button>
            </Box>
          </ContentCard>

          {commitmentType === "subcontract" && (
            <ContentCard elevation={0} p={3}>
              <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                Create Subcontract
              </Typography>

              {/* General Information Section Subcontract Form */}
              <Typography variant="h6" gutterBottom>
                General Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={3} mb={2}>
                <Grid item sx={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Contract Number
                  </Typography>
                  <TextField
                    sx={inputStyle}
                    id="contractNumber"
                    name="contractNumber"
                    fullWidth
                    autoComplete="off"
                    value={localCommitments?.contractNumber || ""}
                    placeholder="Enter Contract Number"
                    InputLabelProps={{ shrink: false }}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item sx={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Contract Company
                  </Typography>
                  <FormControl sx={selectStyle} size="small" fullWidth>
                    <SearchableSelect
                      name="contractCompany"
                      value={formData.commitments?.contractCompany || ""}
                      displayEmpty
                      onChange={handleChange}
                      renderValue={(value) =>
                        value === "" ? (
                          <span style={{ color: "#9e9e9e" }}>Select Company</span>
                        ) : (
                          subcontractDropdownOptions.contractCompany.find(
                            (item) => item.id === value
                          )?.company || value
                        )
                      }
                    >
                      <MenuItem value="" disabled={true} color="text.secondary">
                        <em>Select Company</em>
                      </MenuItem>
                      {subcontractDropdownOptions.contractCompany?.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.company}
                        </MenuItem>
                      ))}
                    </SearchableSelect>
                  </FormControl>
                </Grid>
                <Grid item sx={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Title
                  </Typography>
                  <TextField
                    sx={inputStyle}
                    id="title"
                    name="title"
                    fullWidth
                    autoComplete="off"
                    value={localCommitments?.title || ""}
                    placeholder="Enter Title"
                    InputLabelProps={{ shrink: false }}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={2}>
                <Grid item xs={12}>
                  <RequiredLabel label="Status" />
                  <FormControl sx={selectStyle} size="small" fullWidth>
                    <SearchableSelect
                      name="status"
                      value={formData.commitments?.status || ""}
                      displayEmpty
                      onChange={handleChange}
                      renderValue={(value) =>
                        value === "" ? (
                          <span style={{ color: "#9e9e9e" }}>Select Company</span>
                        ) : (
                          safeDisplay(
                            subcontractDropdownOptions.status?.find((item) => item.id === value)
                              ?.name || value
                          )
                        )
                      }
                    >
                      <MenuItem value="" disabled={true} color="text.secondary">
                        <em>Select Status</em>
                      </MenuItem>
                      {subcontractDropdownOptions.status?.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {safeDisplay(item.name)}
                        </MenuItem>
                      ))}
                    </SearchableSelect>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Default Retainage
                  </Typography>
                  <TextField
                    sx={inputStyle}
                    id="defaultRetainage"
                    name="defaultRetainage"
                    fullWidth
                    autoComplete="off"
                    value={localCommitments?.defaultRetainage || ""}
                    placeholder="Enter Default Retainage %"
                    InputLabelProps={{ shrink: false }}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <RequiredLabel label="Executed" />
                  <FormControlLabel
                    sx={{
                      m: 0,
                      height: 35,
                      display: "flex",
                      alignItems: "center",
                    }}
                    control={
                      <Checkbox
                        name="executed"
                        checked={formData.commitments?.executed || false}
                        onChange={handleChange}
                        sx={{ pt: 3, p: 0 }}
                      />
                    }
                  />
                </Grid>
              </Grid>

              <AttachFilesSection />

              <ScheduleOfValuesSection>
                <SubcontractLineItemsTable
                  rows={scheduleRows}
                  onRowsChange={setScheduleRows}
                  onFormDataChange={(updatedRows) => {
                    setFormData((prev) => ({
                      ...prev,
                      commitments: {
                        ...prev.commitments,
                        scheduleOfValues: updatedRows,
                      },
                    }));
                  }}
                  dropdownOptions={subcontractDropdownOptions}
                  getDisplayText={getDisplayText}
                />
              </ScheduleOfValuesSection>

              {/* Inclusions & Exclusions Section Subcontract Form*/}
              <Typography variant="h6" gutterBottom>
                Inclusions & Exclusions
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={25} mb={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Start Date
                  </Typography>
                  <DatePicker
                    value={
                      formData.commitments?.startDate ? dayjs(formData.commitments.startDate) : null
                    }
                    onChange={(newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        commitments: {
                          ...prev.commitments,
                          startDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                        },
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        placeholder="yyyy-mm-dd"
                        sx={dateStyle}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Estimated Completion Date
                  </Typography>
                  <DatePicker
                    value={
                      formData.commitments?.estimatedDate
                        ? dayjs(formData.commitments.estimatedDate)
                        : null
                    }
                    onChange={(newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        commitments: {
                          ...prev.commitments,
                          estimatedDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                        },
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        placeholder="yyyy-mm-dd"
                        sx={dateStyle}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Actual Completion Date
                  </Typography>
                  <DatePicker
                    value={
                      formData.commitments?.actualDate
                        ? dayjs(formData.commitments.actualDate)
                        : null
                    }
                    onChange={(newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        commitments: {
                          ...prev.commitments,
                          actualDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                        },
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        placeholder="yyyy-mm-dd"
                        sx={dateStyle}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={25} mb={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Contract Date
                  </Typography>
                  <DatePicker
                    value={
                      formData.commitments?.contractDate
                        ? dayjs(formData.commitments.contractDate)
                        : null
                    }
                    onChange={(newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        commitments: {
                          ...prev.commitments,
                          contractDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                        },
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        placeholder="yyyy-mm-dd"
                        sx={dateStyle}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Signed Contract Received Date
                  </Typography>
                  <DatePicker
                    value={
                      formData.commitments?.receivedDate
                        ? dayjs(formData.commitments.receivedDate)
                        : null
                    }
                    onChange={(newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        commitments: {
                          ...prev.commitments,
                          receivedDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                        },
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        placeholder="yyyy-mm-dd"
                        sx={dateStyle}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Issued On Date
                  </Typography>
                  <DatePicker
                    value={
                      formData.commitments?.issuedDate
                        ? dayjs(formData.commitments.issuedDate)
                        : null
                    }
                    onChange={(newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        commitments: {
                          ...prev.commitments,
                          issuedDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                        },
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        fullWidth
                        placeholder="yyyy-mm-dd"
                        sx={dateStyle}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* Contract Privacy Section Subcontract Form*/}
              <Typography variant="h6" gutterBottom>
                Contract Privacy
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={3} mb={2} mt={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle2">Private</Typography>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Tooltip
                        title="Using the privacy setting allows only project admins and the select non-admin users access"
                        arrow
                      >
                        <Checkbox
                          name="private"
                          checked={formData.commitments?.private || false}
                          onChange={handleChange}
                        />
                      </Tooltip>
                    }
                    label=""
                  />
                </Box>
              </Grid>
              <Grid container spacing={3} mb={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Access for Non-Admin Users</Typography>
                  <FormControl sx={selectStyle} size="small" fullWidth>
                    <SearchableSelect
                      name="access"
                      value={formData.commitments?.access || ""}
                      displayEmpty
                      onChange={handleChange}
                      renderValue={(value) =>
                        value === "" ? (
                          <span style={{ color: "#9e9e9e" }}>Select Values</span>
                        ) : (
                          value
                        )
                      }
                    >
                      <MenuItem value="" disabled={true} color="text.secondary">
                        <em>Select Values</em>
                      </MenuItem>
                    </SearchableSelect>
                  </FormControl>
                </Grid>
                <Grid item xs={12} mt={2.7}>
                  <FormControlLabel
                    sx={{
                      m: 0,
                      height: 35,
                      display: "flex",
                      alignItems: "center",
                    }}
                    control={
                      <Checkbox
                        name="allow"
                        checked={formData.commitments?.allow || false}
                        onChange={handleChange}
                      />
                    }
                    label="Allow these non-admin users to view the SOV items"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={2}>
                <Typography variant="subtitle2">Invoice Contacts</Typography>
              </Grid>
            </ContentCard>
          )}

          {commitmentType === "purchase_order" && (
            <ContentCard elevation={0} p={3}>
              <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                Create Purchase Order
              </Typography>

              {/* General Information Section Purchase Order */}
              <Typography variant="h6" gutterBottom>
                General Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={3} mb={2}>
                <Grid item sx={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Contract Number
                  </Typography>
                  <TextField
                    sx={inputStyle}
                    id="purchaseContractNumber"
                    name="purchaseContractNumber"
                    fullWidth
                    autoComplete="off"
                    value={localCommitments?.purchaseContractNumber || ""}
                    placeholder="Enter Contract Number"
                    InputLabelProps={{ shrink: false }}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item sx={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Contract Company
                  </Typography>
                  <FormControl sx={selectStyle} size="small" fullWidth>
                    <SearchableSelect
                      name="purchaseContractCompany"
                      value={formData.commitments?.purchaseContractCompany || ""}
                      displayEmpty
                      onChange={handleChange}
                      renderValue={(value) => {
                        if (value === "" || !value) {
                          return <span style={{ color: "#9e9e9e" }}>Select Company</span>;
                        }
                        const foundItem =
                          purchaseOrderDropdownOptions.purchaseContractCompany?.find(
                            (item) => item.id === value
                          );
                        return foundItem?.company || String(value);
                      }}
                    >
                      <MenuItem value="" disabled={true} color="text.secondary">
                        <em>Select Company</em>
                      </MenuItem>
                      {purchaseOrderDropdownOptions.purchaseContractCompany?.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.company}
                        </MenuItem>
                      ))}
                    </SearchableSelect>
                  </FormControl>
                </Grid>
                <Grid item sx={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Title
                  </Typography>
                  <TextField
                    sx={inputStyle}
                    id="purchaseTitle"
                    name="purchaseTitle"
                    fullWidth
                    autoComplete="off"
                    value={localCommitments?.purchaseTitle || ""}
                    placeholder="Enter Title"
                    InputLabelProps={{ shrink: false }}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={2}>
                <Grid item xs={12}>
                  <RequiredLabel label="Status" />
                  <FormControl sx={selectStyle} size="small" fullWidth>
                    <SearchableSelect
                      name="purchaseStatus"
                      value={formData.commitments?.purchaseStatus || ""}
                      displayEmpty
                      onChange={handleChange}
                      renderValue={(value) => {
                        if (value === "" || !value) {
                          return <span style={{ color: "#9e9e9e" }}>Select Status</span>;
                        }

                        const id = typeof value === "object" ? value.id : value;
                        const foundItem = purchaseOrderDropdownOptions.purchaseStatus.find(
                          (item) => item.value === id
                        );
                        return safeDisplay(foundItem?.name ?? id);
                      }}
                    >
                      <MenuItem value="" disabled={true} color="text.secondary">
                        <em>Select Status</em>
                      </MenuItem>
                      {purchaseOrderDropdownOptions.purchaseStatus?.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {safeDisplay(item.name)}
                        </MenuItem>
                      ))}
                    </SearchableSelect>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <RequiredLabel label="Executed" />
                  <FormControlLabel
                    sx={{
                      height: 35,
                    }}
                    control={
                      <Checkbox
                        name="purchaseExecuted"
                        checked={formData.commitments?.purchaseExecuted || false}
                        onChange={handleChange}
                      />
                    }
                  />
                </Grid>
              </Grid>

              <Grid container spacing={55} mb={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Default Retainage
                  </Typography>
                  <TextField
                    sx={inputStyle}
                    id="purchaseDefaultRetainage"
                    name="purchaseDefaultRetainage"
                    fullWidth
                    autoComplete="off"
                    value={localCommitments?.purchaseDefaultRetainage || ""}
                    placeholder="Enter Default Retainage %"
                    InputLabelProps={{ shrink: false }}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Assigned To
                  </Typography>
                  <FormControl sx={selectStyle} size="small" fullWidth>
                    <SearchableSelect
                      name="purchaseAssignedTo"
                      value={formData.commitments?.purchaseAssignedTo || ""}
                      displayEmpty
                      onChange={handleChange}
                      renderValue={(value) => {
                        if (value === "" || !value) {
                          return <span style={{ color: "#9e9e9e" }}>Select Status</span>;
                        }

                        const id = typeof value === "object" ? value.id : value;
                        const foundItem = purchaseOrderDropdownOptions.purchaseAssignedTo.find(
                          (item) => item.id === id
                        );
                        return safeDisplay(foundItem?.name ?? id);
                      }}
                    >
                      <MenuItem value="" disabled={true} color="text.secondary">
                        <em>Select a Person</em>
                      </MenuItem>
                      {purchaseOrderDropdownOptions.purchaseAssignedTo?.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {safeDisplay(item.name)}
                        </MenuItem>
                      ))}
                    </SearchableSelect>
                  </FormControl>
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Bill To
                  </Typography>
                  <TextField
                    required
                    size="small"
                    sx={{
                      width: 612,
                    }}
                    id="billTo"
                    name="billTo"
                    multiline
                    rows={2}
                    fullWidth
                    value={localCommitments?.billTo || ""}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Payment Terms
                  </Typography>
                  <TextField
                    sx={{
                      width: 612,
                      "& .MuiInputBase-root": {
                        height: 36,
                      },
                      "& .MuiInputBase-input": {
                        padding: "0 12px",
                      },
                    }}
                    id="paymentTerms"
                    name="paymentTerms"
                    fullWidth
                    autoComplete="off"
                    value={localCommitments?.paymentTerms || ""}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Ship To
                  </Typography>
                  <TextField
                    required
                    size="small"
                    sx={{
                      width: 612,
                    }}
                    id="shipTo"
                    name="shipTo"
                    multiline
                    rows={2}
                    fullWidth
                    value={localCommitments?.shipTo || ""}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Ship Via
                  </Typography>
                  <TextField
                    sx={{
                      width: 612,
                      "& .MuiInputBase-root": {
                        height: 36,
                      },
                      "& .MuiInputBase-input": {
                        padding: "0 12px",
                      },
                    }}
                    id="shipVia"
                    name="shipVia"
                    fullWidth
                    autoComplete="off"
                    value={localCommitments?.shipVia || ""}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

              <Grid container mb={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Description
                  </Typography>
                  <TextField
                    sx={{
                      width: 1248,
                    }}
                    id="purchaseDescription"
                    name="purchaseDescription"
                    rows={4}
                    fullWidth
                    multiline
                    autoComplete="off"
                    value={localCommitments?.purchaseDescription || ""}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

              <AttachFilesSection showLabel label="Attachments" />

              <ScheduleOfValuesSection>
                <PurchaseOrderLineItemsTable
                  rows={scheduleRows}
                  onRowsChange={setScheduleRows}
                  onFormDataChange={(updatedRows) => {
                    setFormData((prev) => ({
                      ...prev,
                      commitments: {
                        ...prev.commitments,
                        scheduleOfValues: updatedRows,
                      },
                    }));
                  }}
                  dropdownOptions={purchaseOrderDropdownOptions}
                  getDisplayText={getDisplayText}
                />
              </ScheduleOfValuesSection>

              {/* Contract Dates Section Purchase Order */}
              <Typography variant="h6" gutterBottom>
                Contract Dates
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={3} mb={2}>
                <Grid container spacing={25} mb={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Contract Date
                    </Typography>
                    <DatePicker
                      value={
                        formData.commitments?.purchaseContractDate
                          ? dayjs(formData.commitments.purchaseContractDate)
                          : null
                      }
                      onChange={(newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          commitments: {
                            ...prev.commitments,
                            purchaseContractDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                          },
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          placeholder="yyyy-mm-dd"
                          sx={dateStyle}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Delivery Date
                    </Typography>
                    <DatePicker
                      value={
                        formData.commitments?.purchaseDeliveryDate
                          ? dayjs(formData.commitments.purchaseDeliveryDate)
                          : null
                      }
                      onChange={(newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          commitments: {
                            ...prev.commitments,
                            purchaseDeliveryDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                          },
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          placeholder="yyyy-mm-dd"
                          sx={dateStyle}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Signed Purchase Order Received Date
                    </Typography>
                    <DatePicker
                      value={
                        formData.commitments?.purchaseReceivedDate
                          ? dayjs(formData.commitments.purchaseReceivedDate)
                          : null
                      }
                      onChange={(newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          commitments: {
                            ...prev.commitments,
                            purchaseReceivedDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                          },
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          placeholder="yyyy-mm-dd"
                          sx={dateStyle}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={25} mb={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Issued On Date
                    </Typography>
                    <DatePicker
                      value={
                        formData.commitments?.purchaseIssuedDate
                          ? dayjs(formData.commitments.purchaseIssuedDate)
                          : null
                      }
                      onChange={(newValue) => {
                        setFormData((prev) => ({
                          ...prev,
                          commitments: {
                            ...prev.commitments,
                            purchaseIssuedDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                          },
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          placeholder="yyyy-mm-dd"
                          sx={dateStyle}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Contact Privacy Section Purchase Order */}
              <Typography variant="h6" gutterBottom>
                Contract Privacy
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={3} mb={2} mt={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle2">Private</Typography>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Tooltip
                        title="Using the privacy setting allows only project admins and the select non-admin users access"
                        arrow
                      >
                        <Checkbox
                          name="private"
                          checked={formData.commitments?.private || false}
                          onChange={handleChange}
                        />
                      </Tooltip>
                    }
                    label=""
                  />
                </Box>
              </Grid>
              <Grid container spacing={3} mb={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Access for Non-Admin Users</Typography>
                  <FormControl sx={selectStyle} size="small" fullWidth>
                    <SearchableSelect
                      name="purchaseAccess"
                      value={formData.commitments?.purchaseAccess || ""}
                      displayEmpty
                      onChange={handleChange}
                      renderValue={(value) =>
                        value === "" ? (
                          <span style={{ color: "#9e9e9e" }}>Select Values</span>
                        ) : (
                          value
                        )
                      }
                    >
                      <MenuItem value="" disabled={true} color="text.secondary">
                        <em>Select Values</em>
                      </MenuItem>
                    </SearchableSelect>
                  </FormControl>
                </Grid>
                <Grid item xs={12} mt={2.7}>
                  <FormControlLabel
                    sx={{
                      m: 0,
                      height: 35,
                      display: "flex",
                      alignItems: "center",
                    }}
                    control={
                      <Checkbox
                        name="purchaseAllow"
                        checked={formData.commitments?.purchaseAllow || false}
                        onChange={handleChange}
                      />
                    }
                    label="Allow these non-admin users to view the SOV items"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={2}>
                <Typography variant="subtitle2">Invoice Contacts</Typography>
              </Grid>
            </ContentCard>
          )}

          {commitmentType === null && (
            <ContentCard elevation={0} p={3}>
              <Typography variant="body1" color="text.secondary">
                Please select a commitment type above to begin creating a form.
              </Typography>
            </ContentCard>
          )}
        </Box>
      );
    };

    return <Box>{renderForm()}</Box>;
  }
);

CreateCommitmentsForm.propTypes = {
  formData: PropTypes.shape({
    commitments: PropTypes.object,
  }).isRequired,
  setFormData: PropTypes.func.isRequired,
  commitmentType: PropTypes.string.isRequired,
  setCommitmentType: PropTypes.func.isRequired,
  purchaseOrderDropdownOptions: PropTypes.object.isRequired,
  subcontractDropdownOptions: PropTypes.object.isRequired,
  selectedProject: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    company: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    company_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  setRefreshCommitments: PropTypes.func.isRequired,
};

export default CreateCommitmentsForm;
